import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio, Checkbox, Col, DatePicker, Input, InputNumber, Row, Space, Switch } from 'antd';
import _, { map } from 'lodash';
import moment from 'moment';
import { dateToDayjs } from '@/utils';
import { childCharacteristics } from '@/stores/data';
import {
  useGetAdoptionLiaisonsFamilyQuery,
  useGetCountiesQuery,
  useGetSiblingTypesQuery,
  useGetGendersQuery,
  useGetContactTypesQuery,
  useGetLeadInquiryReasonsQuery,
  useGetLeadSourcesQuery,
  useGetNameSuffixesQuery,
  useGetPhoneTypesQuery,
  useGetRacesQuery,
  useGetStatesQuery,
  useGetCountryAdoptionLiaisonFamilyMutation,
  useGetAddressLookupMutation,
} from '@/services/general-api';
import { useSaveParentMutation } from '@/services/parent-api';
import {
  AddressBookSvg,
  BriefcaseSvg,
  CheckSvg,
  ChildSvg,
  MapMarkerAltSvg,
  NewspaperSvg,
  SaveSvg,
  UserFriendsSvg,
  UserSvg,
} from '@/assets/icons';
import Select from '@/components/select';
import { Select as AntSelect } from 'antd';
import Panel from '@/components/panel';
import FixedFooter from '@/components/fixed-footer';
import Button from '@/components/button';
import Loading from '@/components/loading';
import PhoneInput from '@/components/phone-input';
import { Form } from './styles';
import LocationModal from './location-modal';

const leadSourceIdsDetailNotRequired = [24611, 24598, 24599];

/**
 * A hook to debounce a value over a given delay.
 * @param value The value to debounce.
 * @param delay The delay in milliseconds.
 * @returns The debounced value.
 */
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const EditForm = ({ parentData }) => {
  const navigate = useNavigate();

  const { data: phoneTypeData } = useGetPhoneTypesQuery();
  const { data: suffixData, isLoading: loadingSuffixData } = useGetNameSuffixesQuery();
  const { data: raceData, isLoading: loadingRaceData } = useGetRacesQuery();
  const { data: stateData, isLoading: loadingStateData } = useGetStatesQuery();
  const { data: countryData, isLoading: loadingCountryData } = useGetCountiesQuery();
  const { data: liaisonData, isLoading: loadingLiaisonData } = useGetAdoptionLiaisonsFamilyQuery();
  const { data: leadSourceData, isLoading: loadingLeadSourceData } = useGetLeadSourcesQuery();
  const { data: contactTypeData, isLoading: loadingContactTypeData } = useGetContactTypesQuery();
  const { data: reasonData, isLoading: loadingReasonData } = useGetLeadInquiryReasonsQuery();
  const { data: genderData, isLoading: loadingGenderData } = useGetGendersQuery();
  const { data: siblingTypeData, isLoading: loadingSiblingTypeData } = useGetSiblingTypesQuery();
  const [getAddressLookup, { isLoading: lookingupAddress }] = useGetAddressLookupMutation();
  const [getAdoption] = useGetCountryAdoptionLiaisonFamilyMutation();
  const [saveParent, { isLoading: savingParent }] = useSaveParentMutation();

  const [selectedRaces, setSelectedRaces] = useState();
  const [form] = Form.useForm();
  const [leadDetailRequired, setLeadDetailRequired] = useState(true);

  // Effect to ensure that selectedRaces is updated when raceData changes
  // useEffect(() => {
  //   if (selectedRaces?.length > 0 && selectedRaces?.length === raceData?.length) {
  //     setSelectedRaces(raceData.map((item) => item.id));
  //   }
  // }, [raceData]);

  const raceOptions = useMemo(() => {
    if(raceData?.length > 0)
      return raceData.map((item) => { return { label: item.name, value: item.id}; });
    return [];
  }, [raceData]);

  const genderOptions = useMemo(() => {
    if(genderData?.length > 0) {
      var map = genderData.map((item) => { return { label: item.name, value: item.id }; });
      map.splice(0, 0, { label: 'Any', value: '' });
      return map;
    }
    return [];
  }, [genderData]);

  const handleSelectAll = useCallback(() => {
    if (selectedRaces?.length !== 0) {
      // If all were selected, clear selection
      setSelectedRaces([]);
      form.setFieldValue(['seeking', 'race', 'values'], []);
    } else {
      // Otherwise select all
      const tmp = raceData.map((item) => item.id);
      setSelectedRaces(tmp);
      form.setFieldValue(['seeking', 'race', 'values'], tmp);
    }
  }, [selectedRaces, raceData, form]);

  const [address, setAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
  });

  const [lookupAddress, setLookupAddress] = useState();
  const [isLocationModalOpen, openLocationModal] = useState(false);
  const [selectedState, setSelectedState] = useState();

  const debouncedAddress = useDebounce(address, 400);

  const validateAddress = async (address) => {
    try {
      let params = {};
      if (address.line1) params = { ...params, line1: address.line1 };
      if (address.zip) params = { ...params, zip: address.zip };
      const response = await getAddressLookup(params);
      if (response?.data?.allPossibleLocations && response?.data?.allPossibleLocations.length > 0) {
        setLookupAddress(response.data);
        openLocationModal(true);
      }
    } catch (error) { }
  };

  const setAddressForm = (address) => {
    if (address?.state == "IN") {
      let countryId = '';
      countryData.forEach((item) => {
        if (item.name === address.county) {
          countryId = item.id;
        }
      });
      form.setFieldValue(['county', 'id'], countryId);
      getAdoption({ countryId }).then(({ data }) => {
        form.setFieldValue(['adoptionLiaison', 'personId'], data.id);
      });
    }
    else {
      form.setFieldValue(['county', 'id'], 4183);
      form.setFieldValue(['adoptionLiaison', 'personId'], 28765);
    }
    form.setFieldsValue({
      address: address,
    });
  };

  useEffect(() => {
    if (debouncedAddress?.line1 && debouncedAddress?.zip?.length >= 5) {
      validateAddress(debouncedAddress);
    }
  }, [debouncedAddress]);

  const initialValues = useMemo(() => {
    if (!phoneTypeData) return {};
    if (!parentData) {
      return {
        contact: {
          phones: phoneTypeData.map((phoneType) => ({ phoneType })),
        },
      };
    }
    const values = _.cloneDeep(parentData);
    values.primary.birthInfo.date = dateToDayjs(values.primary.birthInfo.date);
    if (values.significantOther) {
      values.significantOther.birthInfo.date = dateToDayjs(values.significantOther.birthInfo.date);
    }
    values.seeking.race.values = map(values.seeking.race.values, 'id');
    values.seeking.gender.values = values.seeking.gender.values[0]?.id ?? '';
    values.contact.phones = phoneTypeData.map(
      (phoneType) => values.contact.phones.find((item) => item.phoneType.id === phoneType.id) || { phoneType },
    );
    console.log('edit-form :>> parentData', parentData);
    return values;
  }, [parentData, phoneTypeData]);

  const onFinish = async (body) => {
    body.primary.birthInfo.date = body.primary.birthInfo.date?.toISOString();
    if (body.significantOther) {
      body.significantOther.birthInfo.date = body.significantOther.birthInfo.date?.toISOString();
    }
    body.seeking.race.values = body.seeking.race.values?.map((id) => ({ id }));
    body.seeking.gender.values = ((v) => !v ? [] : [{ id: v }])(body.seeking.gender.values);//?.map((id) => ({ id }));
    body.contact.phones.forEach((phone) => {
      if (phone.number) phone.number = `${phone.number}`;
    });

    try {
      const res = await saveParent({ parentId: parentData?.parentId, body }).unwrap();
      navigate(`/parent/${res.parentId}/view`);
    } catch (err) { }
  };

  if (!phoneTypeData) {
    return <Loading padding={64} />;
  }

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        onValuesChange={(changedValues, allValues) => {
          // Check if the changes are within the 'address' namespace
          //console.log("on fields change : ", changedValues, allValues);

          if(changedValues.address) {
            if (changedValues.address.line1 || changedValues.address.zip) {
              setAddress((prev) => ({
                ...prev,
                ...changedValues.address,
              }));
            }
          }
          

        }}
      >
        <Panel icon={<UserSvg />} title="Primary">
          <Row gutter={32}>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['primary', 'name', 'first']} label="First Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['primary', 'name', 'last']} label="Last Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['primary', 'name', 'suffix']} label="Suffix">
                <Select options={suffixData} valueName="code" loading={loadingSuffixData} allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['primary', 'birthInfo', 'date']} label="Birth Date">
                <DatePicker
                  format="MM/DD/YYYY"
                  onBlur={(e) => {
                    const inputVal = e.target.value;
                    const validDate = moment(inputVal, ['M/D/YYYY', 'MM/DD/YYYY'], true);
                    if (validDate.isValid()) {
                      // Correctly updating only the specific nested date field
                      form.setFieldsValue({
                        primary: {
                          ...form.getFieldValue('primary'),
                          birthInfo: { ...form.getFieldValue(['primary', 'birthInfo']), date: validDate },
                        },
                      });
                    } else {
                      // Setting the date to null without affecting other fields
                      form.setFieldsValue({
                        primary: {
                          ...form.getFieldValue('primary'),
                          birthInfo: { ...form.getFieldValue(['primary', 'birthInfo']), date: null },
                        },
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['primary', 'race', 'id']} label="Race/Ethnicity">
                <Select
                  options={raceData}
                  loading={loadingRaceData}
                  filterOption={(input, option) => option.name.toLowerCase().startsWith(input.toLowerCase())}
                  allowClear
                  showSearch
                />
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel
          title={
            <>
              <UserSvg />
              <span style={{ marginRight: 'auto' }}>Significant Other</span>
              <Form.Item name="hasSignificantOther" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </>
          }
        >
          <Row gutter={32}>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.hasSignificantOther !== currentValues.hasSignificantOther
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('hasSignificantOther') && (
                  <>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item
                        name={['significantOther', 'name', 'first']}
                        label="First Name"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item
                        name={['significantOther', 'name', 'last']}
                        label="Last Name"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item name={['significantOther', 'name', 'suffix']} label="Suffix">
                        <Select options={suffixData} valueName="code" loading={loadingSuffixData} allowClear />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item name={['significantOther', 'birthInfo', 'date']} label="Birth Date">
                        <DatePicker
                          format="MM/DD/YYYY"
                          onBlur={(e) => {
                            const inputVal = e.target.value;
                            const validDate = moment(inputVal, ['M/D/YYYY', 'MM/DD/YYYY'], true);
                            if (validDate.isValid()) {
                              // Correctly updating only the specific nested date field
                              form.setFieldsValue({
                                significantOther: {
                                  ...form.getFieldValue('significantOther'),
                                  birthInfo: {
                                    ...form.getFieldValue(['significantOther', 'birthInfo']),
                                    date: validDate,
                                  },
                                },
                              });
                            } else {
                              // Setting the date to null without affecting other fields
                              form.setFieldsValue({
                                significantOther: {
                                  ...form.getFieldValue('significantOther'),
                                  birthInfo: { ...form.getFieldValue(['significantOther', 'birthInfo']), date: null },
                                },
                              });
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item name={['significantOther', 'race', 'id']} label="Race/Ethnicity">
                        <Select options={raceData} loading={loadingRaceData} allowClear showSearch />
                      </Form.Item>
                    </Col>
                  </>
                )
              }
            </Form.Item>
          </Row>
        </Panel>

        <Panel icon={<MapMarkerAltSvg />} title="Address" loading={lookingupAddress}>
          <Row gutter={32}>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['address', 'line1']} label="Address" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['address', 'line2']} label="Apt/Suite">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['address', 'zip']} label="Zip" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['address', 'city']} label="City" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['address', 'state']} label="State">
                <Select
                  options={stateData}
                  valueName="code"
                  loading={loadingStateData}
                  onChange={(val) => {
                    setSelectedState(val);
                  }}
                  allowClear
                  showSearch
                />
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel icon={<AddressBookSvg />} title="Contact">
          <Row gutter={32}>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['contact', 'email', 'primary']} label="Primary Email" rules={[{ type: 'email' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['contact', 'email', 'secondary']} label="Secondary Email" rules={[{ type: 'email' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['contact', 'email', 'magikEmail']} label="Magik Email" rules={[{ type: 'email' }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={32}>
            <Form.List name={['contact', 'phones']}>
              {(fields) =>
                fields.map((field) => {
                  const type = phoneTypeData[field.key];
                  const isFax = type.code === 'fax';
                  return (
                    <Col xs={24} md={12} xl={8} key={field.key}>
                      <div style={{ display: 'flex', gap: 15 }}>
                        <Form.Item
                          name={[field.name, 'number']}
                          label={`${type.name}${isFax ? '' : ' Phone'}`}
                          style={{ flex: 1 }}
                          rules={[
                            {
                              validator: async (_, value) => {
                                if (!value || `${value}`.length === 10) return Promise.resolve();
                                return Promise.reject('Invalid phone number');
                              },
                            },
                          ]}
                        >
                          <PhoneInput />
                        </Form.Item>
                        {!isFax && (
                          <Form.Item name={[field.name, 'ext']} label="Ext" style={{ width: 100 }}>
                            <Input />
                          </Form.Item>
                        )}
                      </div>
                    </Col>
                  );
                })
              }
            </Form.List>
          </Row>
        </Panel>

        <Panel icon={<BriefcaseSvg />} title="Liaison">
          <Row gutter={32}>
            <Col xs={24} md={12} xl={8} style={{ display: 'flex', gap: 16 }}>
              <Form.Item
                name={['county', 'id']}
                label="County"
                rules={[{ required: selectedState === 'IN' ? true : false }]}
                style={{ flex: 1 }}
              >
                <Select
                  options={countryData}
                  loading={loadingCountryData}
                  showSearch
                  filterOption={(input, option) => option.name.toLowerCase().startsWith(input.toLowerCase())}
                  onChange={(countryId) => {
                    getAdoption({ countryId }).then(({ data }) => {
                      if (data)
                        form.setFieldValue(['adoptionLiaison', 'personId'], data.id);
                    });
                  }}
                />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.county?.id !== currentValues.county?.id}
              >
                {({ getFieldValue }) => {
                  const id = getFieldValue('county')?.id;
                  const country = _.find(countryData, { id });
                  return (
                    <Form.Item label=" " style={{ width: 70 }}>
                      {country?.region.name}
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item
                name={['adoptionLiaison', 'personId']}
                label="Adoption Liaison"
                rules={[{ required: selectedState === 'IN' ? true : false }]}
              >
                <Select options={liaisonData} loading={loadingLiaisonData} showSearch />
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel icon={<NewspaperSvg />} title="Lead Source">
          <Row gutter={32}>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['lead', 'source', 'id']} label="Lead Source" rules={[{ required: true }]}>
                <Select
                  options={leadSourceData}
                  loading={loadingLeadSourceData}
                  onChange={(value) => {
                    form.setFieldValue(['lead', 'sourceType', 'id'], undefined);
                    setLeadDetailRequired(!leadSourceIdsDetailNotRequired.includes(value));
                  }}
                  showSearch
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.lead?.source.id !== currentValues.lead?.source.id
                }
              >
                {({ getFieldValue }) => {
                  const id = getFieldValue('lead')?.source?.id;
                  const sourceTypes = _.find(leadSourceData, { id })?.sourceTypes || [];
                  return (
                    <Form.Item name={['lead', 'sourceType', 'id']} label="Lead Source Detail" rules={[{ required: leadDetailRequired }]}>
                      <Select options={sourceTypes} allowClear showSearch />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['lead', 'initialContactType', 'id']} label="Initial Contact Type">
                <Select options={contactTypeData} loading={loadingContactTypeData} allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['lead', 'inquiryReason', 'id']} label="Reason" rules={[{ required: true }]}>
                <Select options={reasonData} loading={loadingReasonData} />
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel icon={<ChildSvg />} title="Seeking">
          <Row gutter={32}>
            <Col xs={24} md={12} xl={8}>
              <div style={{ paddingBottom: 8 }}>Age Ranges of Interest</div>
              <Space size="middle" style={{ marginBottom: 24, minHeight: 34 }}>
                <Form.Item name={['seeking', 'ageRange', 'interestedIn_0_5']} valuePropName="checked" noStyle>
                  <Checkbox>0–5</Checkbox>
                </Form.Item>
                <Form.Item name={['seeking', 'ageRange', 'interestedIn_5_9']} valuePropName="checked" noStyle>
                  <Checkbox>5–9</Checkbox>
                </Form.Item>
                <Form.Item name={['seeking', 'ageRange', 'interestedIn_10_12']} valuePropName="checked" noStyle>
                  <Checkbox>10–12</Checkbox>
                </Form.Item>
                <Form.Item name={['seeking', 'ageRange', 'interestedIn_13_Plus']} valuePropName="checked" noStyle>
                  <Checkbox>13+</Checkbox>
                </Form.Item>
              </Space>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['seeking', 'gender', 'values']} label="Gender">
                <Radio.Group options={genderOptions} />
                {/* <Select mode="multiple" options={genderData} loading={loadingGenderData} /> */}
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['seeking', 'siblings', 'id']} label="Sibling Type">
                <Select options={siblingTypeData} loading={loadingSiblingTypeData} allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={24}>
              <a
                style={{ flex: 'none', padding: '4px 8px', display: 'block', cursor: 'pointer', position: 'absolute', margin: '-4px 0px 0px 120px', zIndex: 1 }}
                onClick={handleSelectAll}
              >
                {selectedRaces?.length !== 0 ? 'Deselect All' : 'Select All'}
              </a>
              <Form.Item name={['seeking', 'race', 'values']} label="Race/Ethnicity">
                <Checkbox.Group options={raceOptions} onChange={setSelectedRaces} />
                {/* <Select
                  mode="multiple"
                  value={selectedRaces}
                  onChange={setSelectedRaces}
                  loading={loadingRaceData}
                  options={raceData}
                  fieldNames={{label: 'name', value: 'id'}}
                  dropdownRender={(menu) => (
                    <div>
                      <div style={{ display: 'flex', padding: 8, alignItems: 'center' }}>
                        <a
                          style={{ flex: 'none', padding: '8px', display: 'block', cursor: 'pointer' }}
                          onClick={handleSelectAll}
                        >
                          {selectedRaces?.length === raceData?.length ? 'Deselect All' : 'Select All'}
                        </a>
                      </div>
                      <div>{menu}</div>
                    </div>
                  )}
                /> */}
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel icon={<UserFriendsSvg />} title="Existing Family">
          <Row gutter={32}>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['currentFamily', 'numberOfChildren']} label="Number of Children">
                <InputNumber min={1} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['currentFamily', 'ages']} label="Ages">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel icon={<CheckSvg />} title="Characteristics" style={{ marginBottom: 58 }}>
          <Row gutter={32}>
            {childCharacteristics.map((item) => (
              <Col xs={24} md={12} xl={8} key={item.key}>
                <Form.Item name={['characteristics', item.key]} valuePropName="checked" className="m-0">
                  <Checkbox>{item.label}</Checkbox>
                </Form.Item>
              </Col>
            ))}
          </Row>
          <Form.Item name={['characteristics', 'otherCharacteristic']}>
            <Input.TextArea autoSize={{ minRows: 2 }} />
          </Form.Item>
        </Panel>

        <FixedFooter>
          <Button type="primary" htmlType="submit" icon={<SaveSvg />} loading={savingParent}>
            Save
          </Button>
        </FixedFooter>
      </Form>
      <LocationModal
        open={isLocationModalOpen}
        address={lookupAddress}
        setAddressForm={setAddressForm}
        onClose={() => openLocationModal(false)}
      />
    </>
  );
};

export default EditForm;
